/**
 * Skill Matcher utility — pure function shared between client and server.
 *
 * Searches a list of package skill entries for the best match against a
 * suggested skill using a four-tier strategy:
 *   1. Exact key match
 *   2. Case-insensitive name match
 *   3. Fuzzy substring match against skill names
 *   4. Semantic category match — skill belongs in a category by topic
 *
 * The category match (Tier 4) handles cases like "GraphQL" → "Backend & API"
 * where the skill doesn't exist yet but clearly belongs in a category.
 */

import type { PackageSkillEntry, PlacementResult } from '../types/placement';

export function normaliseKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Category keyword map — maps skill keywords to category name patterns.
 * Used for Tier 4 semantic matching.
 */
const CATEGORY_KEYWORDS: Array<{ skills: string[]; categories: string[] }> = [
  {
    skills: ['graphql', 'rest', 'api', 'grpc', 'openapi', 'swagger', 'webhook', 'soap'],
    categories: ['api', 'backend', 'web service', 'integration', 'interface'],
  },
  {
    skills: ['react', 'vue', 'angular', 'svelte', 'next', 'nuxt', 'html', 'css', 'tailwind', 'sass', 'webpack', 'vite'],
    categories: ['frontend', 'ui', 'web', 'client'],
  },
  {
    skills: ['node', 'express', 'fastify', 'django', 'flask', 'spring', 'rails', 'laravel', 'dotnet'],
    categories: ['backend', 'server', 'api', 'web'],
  },
  {
    skills: ['postgres', 'mysql', 'mongodb', 'redis', 'dynamodb', 'sqlite', 'cassandra', 'elasticsearch'],
    categories: ['database', 'data', 'storage', 'persistence'],
  },
  {
    skills: ['docker', 'kubernetes', 'terraform', 'ansible', 'helm', 'jenkins', 'github actions', 'ci/cd', 'devops'],
    categories: ['devops', 'infrastructure', 'platform', 'deployment', 'cloud ops'],
  },
  {
    skills: ['aws', 'azure', 'gcp', 'lambda', 's3', 'ec2', 'cloudfront', 'cognito', 'amplify'],
    categories: ['cloud', 'aws', 'azure', 'infrastructure'],
  },
  {
    skills: ['typescript', 'javascript', 'python', 'java', 'go', 'rust', 'kotlin', 'swift', 'c#', 'ruby'],
    categories: ['programming', 'language', 'development', 'engineering'],
  },
  {
    skills: ['jest', 'vitest', 'cypress', 'playwright', 'testing', 'tdd', 'bdd', 'unit test'],
    categories: ['testing', 'quality', 'qa'],
  },
  {
    skills: ['git', 'github', 'gitlab', 'bitbucket', 'jira', 'confluence', 'agile', 'scrum', 'kanban'],
    categories: ['tools', 'workflow', 'collaboration', 'process', 'methodology'],
  },
  {
    skills: ['machine learning', 'ai', 'tensorflow', 'pytorch', 'llm', 'openai', 'bedrock', 'nlp'],
    categories: ['ai', 'machine learning', 'data science', 'ml'],
  },
  {
    skills: ['figma', 'sketch', 'ux', 'ui design', 'accessibility', 'wcag', 'design system'],
    categories: ['design', 'ux', 'ui', 'product'],
  },
];

/**
 * Score how well a skill name matches a category name semantically.
 * Returns a confidence score 0-1, or 0 if no match.
 */
function scoreCategoryMatch(skillName: string, categoryName: string): number {
  const lowerSkill = skillName.toLowerCase();
  const lowerCategory = categoryName.toLowerCase();

  for (const { skills, categories } of CATEGORY_KEYWORDS) {
    const skillMatches = skills.some(s => lowerSkill.includes(s) || s.includes(lowerSkill));
    const categoryMatches = categories.some(c => lowerCategory.includes(c) || c.includes(lowerCategory));

    if (skillMatches && categoryMatches) {
      // Higher confidence if both are strong matches
      const skillScore = skills.reduce((best, s) => {
        if (lowerSkill === s) return 1;
        if (lowerSkill.includes(s) || s.includes(lowerSkill)) return Math.max(best, 0.8);
        return best;
      }, 0);
      return skillScore;
    }
  }
  return 0;
}

export function findSkillPlacement(
  suggestedName: string,
  suggestedKey: string,
  packageSkills: PackageSkillEntry[],
): PlacementResult {
  const normSuggestedKey = normaliseKey(suggestedKey);
  const lowerSuggestedName = suggestedName.toLowerCase();

  // --- Tier 1: Exact key match ---
  for (const entry of packageSkills) {
    if (entry.skillKey !== null && normaliseKey(entry.skillKey) === normSuggestedKey) {
      return {
        matchType: 'exact',
        skillId: entry.skillId,
        skillKey: entry.skillKey,
        packageName: entry.packageName,
        categoryName: entry.categoryName,
        source: entry.source,
      };
    }
  }

  // --- Tier 2: Case-insensitive name match ---
  for (const entry of packageSkills) {
    if (entry.skillName.toLowerCase() === lowerSuggestedName) {
      return {
        matchType: 'exact',
        skillId: entry.skillId,
        skillKey: entry.skillKey ?? undefined,
        packageName: entry.packageName,
        categoryName: entry.categoryName,
        source: entry.source,
      };
    }
  }

  // --- Tier 3: Fuzzy substring match against skill names ---
  let bestSkillCandidate: PlacementResult | null = null;
  let bestSkillConfidence = -1;

  for (const entry of packageSkills) {
    const lowerEntryName = entry.skillName.toLowerCase();
    const isFuzzy =
      lowerSuggestedName.includes(lowerEntryName) ||
      lowerEntryName.includes(lowerSuggestedName);

    if (isFuzzy) {
      const minLen = Math.min(suggestedName.length, entry.skillName.length);
      const maxLen = Math.max(suggestedName.length, entry.skillName.length);
      const confidence = minLen / maxLen;

      if (confidence > bestSkillConfidence) {
        bestSkillConfidence = confidence;
        bestSkillCandidate = {
          matchType: 'fuzzy',
          skillId: entry.skillId,
          skillKey: entry.skillKey ?? undefined,
          packageName: entry.packageName,
          categoryName: entry.categoryName,
          confidence,
          source: entry.source,
        };
      }
    }
  }

  if (bestSkillCandidate && bestSkillConfidence > 0.5) {
    return bestSkillCandidate;
  }

  // --- Tier 4: Semantic category match ---
  // Find the best category to place this skill into based on topic keywords.
  // e.g. "GraphQL" → "Backend & API" category in "Professional Core Skills"
  const seenCategories = new Set<string>();
  let bestCategoryCandidate: PlacementResult | null = null;
  let bestCategoryScore = 0;

  for (const entry of packageSkills) {
    const catKey = `${entry.packageId}:${entry.categoryId}`;
    if (seenCategories.has(catKey)) continue;
    seenCategories.add(catKey);

    const score = scoreCategoryMatch(suggestedName, entry.categoryName);
    if (score > bestCategoryScore) {
      bestCategoryScore = score;
      bestCategoryCandidate = {
        matchType: 'category',
        packageName: entry.packageName,
        categoryName: entry.categoryName,
        suggestedCategoryId: entry.categoryId,
        suggestedPackageId: entry.packageId,
        confidence: score,
        source: entry.source,
      };
    }
  }

  // Also check fuzzy skill matches below 0.5 threshold as fallback
  if (bestCategoryCandidate && bestCategoryScore > 0.5) {
    return bestCategoryCandidate;
  }

  // Return best fuzzy skill match even if below threshold, or category match
  if (bestSkillCandidate && bestCategoryCandidate) {
    return bestSkillConfidence >= bestCategoryScore ? bestSkillCandidate : bestCategoryCandidate;
  }
  if (bestSkillCandidate) return bestSkillCandidate;
  if (bestCategoryCandidate && bestCategoryScore > 0) return bestCategoryCandidate;

  // --- No match ---
  return { matchType: 'none' };
}
